/* eslint-disable max-lines-per-function */
import {useContext} from 'react';
import {useRouteMatch} from 'react-router-dom';
import {useSelector, useStore} from 'react-redux';
import cx from 'classnames';
import {SimpleNav} from '~library/organisms/SimpleNav';
import {InjectableMegaNav} from '~library/organisms/InjectableMegaNav';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {eventStepCount} from '~/CreateFabricInvitation/navigation/createSteps';
import {eventIsPremium} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  selectCurrentSection,
  selectUsedPremiumFeature,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectShowLeaveModal} from '~/CreateFabricInvitation/reducers/modals';
import {setShowLeaveModal} from '~/CreateFabricInvitation/reducers/modals/actions';
import {getEventStepsType} from '~/common/fabric/EventGuests/components/DisplayCondition/addGuestsCondition';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {ConfirmLeaveModalContainer} from '~/CreateFabricInvitation/navigation/ConfirmLeaveModal';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {ExitPremiumPreviewModal} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/ExitPremiumPreviewModal/ExitPremiumPreviewModal';
import {useHeaderModals} from './hooks/useHeaderModals';
import {HeaderNavigation} from './components/HeaderNavigation';
import {PremiumPreviewNav} from './components/PremiumPreviewNav';
import {ProgressBar} from './ProgressBar';
import {UPDATE_EVENT_DETAILS} from '~/CreateFabricInvitation/reducers/details/constants';

import './NavHeader.sass';

export const NavHeader = () => {
  const isLargishAndUp = useIsLargishAndUp();
  const currentRoute = useCurrentRoute();
  const store = useStore();
  const showLeaveModal = useSelector(selectShowLeaveModal);
  const isPremium = useSelector(eventIsPremium);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const showSteps = currentRoute.meta.countSteps;
  const currentSection = useSelector(selectCurrentSection);
  const eventId = useSelector(getEventId);
  const hasBeenSaved = !!eventId;
  const isGuestStep = useRouteMatch('/:eventId/guests')?.isExact;
  const eventStepsType = useSelector(getEventStepsType);
  const totalSteps = eventStepCount[eventStepsType];

  const navigate = useNavigator({
    isMobile: !isLargishAndUp,
    disableAutoOpenEditor: currentSection === Sections.CARD_DESIGN,
  });

  const {
    showUpsellModal,
    upsellModalMode,
    showExitPremiumPreviewModal,
    setShowUpsellModal,
    setShowExitPremiumPreviewModal,
    toggleUpsellModal,
    downgradeIfDismissCheckout,
    onClosePremiumPreviewClick,
    upgradeToPremium,
    handleExitPremiumPreview,
    handleShowLeaveModal,
  } = useHeaderModals({store, navigate, currentRoute, isPremium});

  const simpleNavObj = {
    prev: {
      onClick: () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        navigateToRoute('back', store);
      },
      text: 'Back',
      qaId: 'upsell-back',
      buttonVariant: 'secondary',
    },
    next: {
      onClick: () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        navigateToRoute('next', store);
      },
      text: 'Skip',
      qaId: 'upsell-next',
      buttonVariant: 'secondary',
    },
  };

  if (currentRoute.showMegaNav) {
    return <InjectableMegaNav />;
  }

  if (currentRoute.showSimpleNav) {
    return <SimpleNav prev={simpleNavObj.prev} next={simpleNavObj.next} />;
  }

  const onSave = () => store.dispatch(saveEventThunk());
  const stepLabel = showSteps ? `${currentRoute.id} of ${totalSteps}:` : '';
  const showPreviewNav = currentRoute.id === 1 && !isPremium && usedPremiumFeature;
  const showProgressBar = !showPreviewNav;
  const premiumPreview = usedPremiumFeature && !isPremium;
  const {inIsolatedEditMode} = useContext(FabricContext);

  return (
    <>
      <div
        className={cx('fabric-header__wrapper', {
          'fabric-header__wrapper--isolated': inIsolatedEditMode,
        })}
      >
        <header
          className={cx('fabric-header', {
            frp: currentRoute.id > totalSteps,
            'premium-preview-header': showPreviewNav,
          })}
        >
          {showPreviewNav ? (
            <PremiumPreviewNav
              isLargishAndUp={isLargishAndUp}
              onClosePremiumPreviewClick={onClosePremiumPreviewClick}
              toggleUpsellModal={toggleUpsellModal}
            />
          ) : (
            <HeaderNavigation
              currentRoute={currentRoute}
              isLargishAndUp={isLargishAndUp}
              isPremium={isPremium}
              showSteps={showSteps}
              stepLabel={stepLabel}
              isGuestStep={isGuestStep}
              eventId={eventId}
              totalSteps={totalSteps}
              handleShowLeaveModal={handleShowLeaveModal}
              onSave={onSave}
              premiumPreview={premiumPreview}
              store={store}
            />
          )}
        </header>
        {showProgressBar && <ProgressBar currentStepId={currentRoute.id} totalSteps={totalSteps} />}
      </div>

      {showLeaveModal && (
        <ConfirmLeaveModalContainer
          isFirstStep={!hasBeenSaved}
          isEditingFlow={currentRoute.meta.editing}
          isGuestStep
          leave={() => {
            store.dispatch(setShowLeaveModal(false));
            downgradeIfDismissCheckout();
            navigateToRoute('exit', store, true);
          }}
          saveAndLeave={() => {
            const force = currentRoute.meta.creating ? 'saveAndExit' : false;
            store.dispatch(setShowLeaveModal(false));
            navigateToRoute('exit', store, force);
          }}
          cancelLeave={() => store.dispatch(setShowLeaveModal(false))}
        />
      )}

      {showUpsellModal && (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={() => toggleUpsellModal(!showUpsellModal)}
          closeOverlay={() => {
            setShowUpsellModal(false);
            setShowExitPremiumPreviewModal(false);
          }}
          onConfirmAction={upgradeToPremium}
          mode={upsellModalMode}
          onDenyAction={handleExitPremiumPreview}
        />
      )}

      {showExitPremiumPreviewModal && (
        <ExitPremiumPreviewModal
          closeOverlay={() => {
            setShowExitPremiumPreviewModal(false);
          }}
          onConfirmAction={handleExitPremiumPreview}
        />
      )}
    </>
  );
};
